import styled, { css } from "styled-components/macro";
import {
  BREAKPOINT_MOBILE,
  BREAKPOINT_TABLET,
  TOP_PADDING,
  ROW_PADDING,
  ROW_HEIGHT,
  MACHINES_COLUMN_WIDTH_MOBILE,
  MACHINES_COLUMN_WIDTH_TABLET,
  CHART_MAX_WIDTH,
  Z_INDEX,
  CARD_BORDER_RADIUS_CSS,
} from "../../constants/constants";
import {
  darkModeBackgroundColor,
  darkModeCardBackgroundColor,
  lightAccentSuccess10,
  lightAccentSuccess50,
  lightAccentSuccess90,
} from "../../constants/colors";
import {
  MACHINE_BREAKDOWN_METRIC_WIDTH,
  METRICS_COLUMNS_SPACER_WIDTH,
} from "./MachineBreakdownChart/machineBreakdownChartUtils";

export const TimelinesStyles = styled.div<{
  $isDarkMode: boolean;
  $isPending?: boolean;
}>`
  z-index: ${Z_INDEX.L7};
  &.tooltipOpen {
    z-index: ${Z_INDEX.L9};
  }
  position: relative;
  width: 100%;
  padding-bottom: 4em;
  margin-top: 1.5em;
  ${props =>
    props.$isPending &&
    css`
      opacity: 0.5;
    `}
  &.detailsPage {
    padding-bottom: 0;
    padding-top: 1em;
    min-height: 77px; /* prevent layout shift on first load */
  }
  &.isRetrotagging {
    padding-bottom: 1em;
    border-radius: 5px;
    z-index: ${Z_INDEX.L12};
    background-color: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeBackgroundColor : "white"};
    .timelineLeftSide {
      /* white background shows against dark retrotag overlay -- need to hide while retrotagging */
      padding-top: 0px;
      margin-top: 0px;
      padding-left: 0px;
      margin-left: 0px;
    }
  }
  @media print {
    padding-bottom: 0;
  }
  .timeAxisColumn {
    width: 100%;
    &.pending {
      opacity: 0.6 !important;
      cursor: wait;
    }
  }
  .timelineContainer {
    padding-top: 36px;
    align-items: stretch;
    .processesColumn {
      width: 90px;
      padding-left: 50px;
    }
  }
  .flex {
    display: flex;
    .col {
      flex-direction: column;
    }
    .row {
      flex-direction: row;
    }
  }
  .flexGrow1 {
    flex-grow: 1;
  }
  .textAlignRight {
    text-align: right;
  }
  .spacer {
    width: ${METRICS_COLUMNS_SPACER_WIDTH}px;
    min-width: ${METRICS_COLUMNS_SPACER_WIDTH}px;
    height: ${METRICS_COLUMNS_SPACER_WIDTH}px;
  }
  .metricTitle {
    width: ${MACHINE_BREAKDOWN_METRIC_WIDTH}px;
    text-align: right;
    word-break: break-word;
    height: 1em;
  }
  .timelineLeftSide {
    padding-right: 10px;
    /* hide the timeline overflow behind this element after dragging and before it re-renders */
    padding-top: 30px;
    margin-top: -30px;
    padding-left: 28px;
    margin-left: -28px;
    display: flex;
    /* when you drag timeline left, need to hide segments overflowing left from.timeAxisColumn */
    z-index: 1;
    background: ${({ $isDarkMode }) =>
      $isDarkMode ? darkModeCardBackgroundColor : "white"};
  }
`;

export const TIME_AXIS_MARGIN_HZ = 28;

export const TimeAxisStyles = styled.div<{
  $isDarkMode: boolean;
  $hideControls?: boolean;
}>`
  overflow: visible;
  font-family: "Roboto", sans-serif;
  background: ${({ $isDarkMode }) =>
    $isDarkMode ? darkModeCardBackgroundColor : "white"};
  position: relative;
  z-index: ${Z_INDEX.L12};
  ${CARD_BORDER_RADIUS_CSS}
  padding: 0 ${TIME_AXIS_MARGIN_HZ / 2}px 0 4px;
  @media (min-width: ${BREAKPOINT_MOBILE}px) {
    padding: 0 ${TIME_AXIS_MARGIN_HZ}px;
  }
  display: flex;
  &.detailsPage {
    svg {
      overflow: visible;
    }
    padding: 0;
  }
  @media print {
    padding: 0 auto;
    width: ${CHART_MAX_WIDTH - 2 * TIME_AXIS_MARGIN_HZ}px;
  }
  ${({ $hideControls }) =>
    $hideControls
      ? css`
          margin-top: 0;
          padding-top: 0;
        `
      : ""}
`;

export const ProcessesColumnStyles = styled.div<{
  $showTagHighlight?: boolean;
}>`
  margin-top: ${TOP_PADDING + 2 * ROW_PADDING + 0.5 * ROW_HEIGHT}px;
  position: relative;
  display: flex;
  flex-direction: column;
  &.machinesColumn {
    width: ${MACHINES_COLUMN_WIDTH_MOBILE}px;
    min-width: ${MACHINES_COLUMN_WIDTH_MOBILE}px;
  }
  &.metricsColumn {
    width: 0px;
    justify-content: start;
  }
  @media (min-width: ${BREAKPOINT_MOBILE}px) {
    &.metricsColumn {
      min-width: ${MACHINE_BREAKDOWN_METRIC_WIDTH}px;
    }
  }
  @media (min-width: ${BREAKPOINT_TABLET}px) {
    &.machinesColumn {
      min-width: ${MACHINES_COLUMN_WIDTH_TABLET}px;
    }
  }

  .MuiBadge-root {
    flex-grow: 1;
  }
  .metricContainer,
  .processNameLinkWrapper {
    margin-bottom: ${props =>
      props.$showTagHighlight ? ROW_HEIGHT - ROW_PADDING : ROW_PADDING}px;
  }
  .metricContainer {
    height: ${ROW_HEIGHT}px;
    box-sizing: border-box;
    display: flex;
    align-items: center;
  }
  .metricContainer {
    text-align: right;
  }
  .title {
    display: flex;
    width: 100%;
    box-sizing: border-box;
    align-items: center;
    position: absolute;
    top: -${ROW_HEIGHT / 2 + ROW_PADDING}px;
    font-size: 15px;
    font-weight: bold;
    justify-content: flex-end;
    &.machineTitle {
      justify-content: flex-start;
    }
  }

  .metricContainer {
    visibility: hidden;
    @media (min-width: ${BREAKPOINT_MOBILE}px) {
      visibility: visible;
    }
  }
  .MuiBadge-badge {
    top: 21px;
    left: -14px;
    svg {
      transform: scale(0.8);
      color: ${lightAccentSuccess90};
      background: ${lightAccentSuccess10};
      border: 1px solid ${lightAccentSuccess50};
      border-radius: 4px;
    }
  }
  @media (max-width: ${BREAKPOINT_MOBILE}px) {
    .MuiBadge-badge {
      top: 20px;
      left: 6px;
      svg {
        transform: scale(0.6);
      }
    }
    .machineNameContainerWithBadge .processName {
      transform: translateX(16px);
    }
  }
`;
